import { TextField } from "@mui/material";
import { useContext, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import * as Yup from "yup";
import AdvancedFilter from "../../../component/AdvancedFilter";
import { addFilter, getFilterValue, renderMultiSelect } from "../../../utils/FilterUtils";
import { ID_FILTER_REPORT_CREATIONDATEFROM, ID_FILTER_REPORT_CREATIONDATETO, ID_FILTER_SEARCH, ID_FILTER_LEGAL_ENTITIES, ID_FILTER_DEALERS, royaltiesReportDefaultValues } from "../constants/RoyaltiesFilters";
import { STATE_REPORT_ACTIVE_FILTERS } from "../constants/RoyaltiesRecoil";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

function ReportsFilter() {

    const { t } = useTranslation('finance', { keyPrefix: 'royalties' });
    const { t: tg } = useTranslation("translation");

    const setFilters = useSetRecoilState(STATE_REPORT_ACTIVE_FILTERS);
    const [search, setSearch] = useState("");
    const filterDefs = useRecoilValue(STATE_REPORT_ACTIVE_FILTERS);
    const {
        globalData: { user },
    } = useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
        value: x.name,
        label: tg(`dealers.${x.name}`),
        }));

    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const schema = Yup.object().shape({
        creationDateFrom: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.creationDateTo || (value <= context.parent.creationDateTo);
        }).typeError(t('filters.errors.invalid-date')),
        creationDateTo: Yup.date().nullable().test("testTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.creationDateFrom || (value >= context.parent.creationDateFrom);
        }).typeError(t('filters.errors.invalid-date')),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne'))
    });

    const defaultValues = { 
        creationDateFrom: getFilterValue(ID_FILTER_REPORT_CREATIONDATEFROM, filterDefs) || null, 
        creationDateTo: getFilterValue(ID_FILTER_REPORT_CREATIONDATETO, filterDefs) || null,
        legalEntities:
            getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) ||
            legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || [],
    };

    const renderCreationDateFrom = (field: any, error: any, state: any, values: any) => (
        <DatePicker label={t('filters.reports.' + ID_FILTER_REPORT_CREATIONDATEFROM)}
                    views={['day']}
                    maxDate={values['creationDateTo'] !== null ? values['creationDateTo'] : null}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth
                                                        id={"creationDateFrom"}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                    />}
                    inputFormat={"L"}
        />
    )

    const renderCreationDateTo = (field: any, error: any, state: any, values: any) => (
        <DatePicker label={t('filters.reports.' + ID_FILTER_REPORT_CREATIONDATETO)}
                    views={['day']}
                    minDate={values['creationDateFrom'] !== null ? values['creationDateFrom'] : null}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth
                                                        id={"creationDateTo"}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                    />}
                    inputFormat={"L"}
        />
    )

    const fields = {
        creationDateFrom: renderCreationDateFrom,
        creationDateTo: renderCreationDateTo,
        legalEntities: renderMultiSelect(
              tg("shared.legalEntities"),
              legalEntityOptions,
              "legalEntities",
              legalEntityOptions.length <= 1,
              (value: any, setValue: any) => {
                setValue('dealers', []);
                let dealers = user?.dealers
                  ?.filter(
                    (x: any) =>
                      value?.length > 0 &&
                      x.legalEntities.some((e: any) => value.includes(e.name)) &&
                      x.name.toLowerCase() !== "all"
                  )
                  .map((x: any) => {
                    return {
                      label: tg(`dealers.${x.name}`),
                      value: x.name,
                    };
                  });
                setDealers(dealers);
              }
            ),
            dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),
    }

    const submit = (data: any) => {
        addFilter(setFilters, ID_FILTER_SEARCH, search);
        addFilter(setFilters, ID_FILTER_REPORT_CREATIONDATEFROM, data.creationDateFrom);
        addFilter(setFilters, ID_FILTER_REPORT_CREATIONDATETO, data.creationDateTo);
        addFilter(setFilters, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_DEALERS, data.dealers);
    }

    const reset = () => {
        setFilters([]);
    }

    return (
        <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
                        searchInput={search} setSearchInput={setSearch}
                        onSubmit={submit}
                        onReset={reset}
                        customDefaultValues={royaltiesReportDefaultValues}
        />
    )
}

export default ReportsFilter;